import { BusinessUser } from '@models/businesses';
import {
	EmailRecipient,
	EmailRecipientType,
	GlobalEmailTemplateMetadata,
} from '@models/global-email-templates';
import { createSelector } from '@ngxs/store';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import {
	EmailTemplateWithUser,
	EmailTemplateWithVersion,
} from '../email-templates.models';
import { GlobalEmailTemplatesStateModel } from './global-email-templates.models';
import { GlobalEmailTemplatesState } from './global-email-templates.state';

export class GlobalEmailTemplatesSelectors {
	static $getTemplates = createSelector(
		[GlobalEmailTemplatesState, BusinessUsersState.getUsers],
		(
			state: GlobalEmailTemplatesStateModel,
			users: BusinessUser[],
		): EmailTemplateWithUser[] => {
			return state.templates.map((template) => ({
				...template,
				source: 'global',
				createdByUser:
					users?.find((user) => user.id === template.createdBy) || null,
			}));
		},
	);

	static $getTemplatesByRecipientFn = (recipient: EmailRecipient) => {
		return createSelector(
			[GlobalEmailTemplatesSelectors.$getTemplates],
			(
				templates: GlobalEmailTemplateMetadata[],
			): GlobalEmailTemplateMetadata[] => {
				return templates.filter((template) => {
					if (recipient.type === EmailRecipientType.ALL) {
						return true;
					}

					if (template.emailRecipientType === recipient.type) {
						if (!recipient.subType) {
							return true;
						}
						return template.emailRecipientSubType === recipient.subType;
					}
					return false;
				});
			},
		);
	};

	static $getTemplateById = createSelector(
		[GlobalEmailTemplatesSelectors.$getTemplates],
		(
			templates: EmailTemplateWithUser[],
		): ((templateId: string) => EmailTemplateWithUser | null) => {
			return (templateId: string) =>
				templates.find((template) => template.id === templateId) || null;
		},
	);

	static $getTemplateByIdWithVersion = createSelector(
		[GlobalEmailTemplatesState, GlobalEmailTemplatesSelectors.$getTemplates],
		(
			state: GlobalEmailTemplatesStateModel,
			templates: EmailTemplateWithUser[],
		): ((templateId: string) => EmailTemplateWithVersion) => {
			return (templateId: string) => {
				const metadata =
					templates.find((template) => template.id === templateId) || null;
				const version =
					metadata?.versionId && metadata.id
						? state.templateVersions[metadata.id]?.[metadata.versionId] || null
						: null;
				return { metadata, version, versionId: metadata?.versionId || null };
			};
		},
	);
}
